import React, { useCallback, useState, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { getVal } from "oolib";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";
import TooltipV2 from "@/components/UI/Tooltip";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import { SearchableLightbox } from "@/components/DataFieldInputSelector/SearchableLightbox";
import { getPickerProp } from "../../utils/pickerVariantConfig";
import { usePieChartConfig } from "./ConfigForm_Context";
import { DATA_KEYS } from "./DATA_KEYS";
import { CollapseTransitionItem } from "../../utils/CollapseTransition";
import { useReportLandingContext } from "../../../../ReportLandingContext";
import { useVisibilityModifier } from "../../utils/useVisibilityModifier";

export const ConfigForm_Dimension1Section = () => {
  const {
    widgetConfig,
    setConfig,
    underEditWidgetIdx,
    optionsForDim1,
    arithmeticState,
  } = usePieChartConfig();

  const {
    dataFields,
    sortedLetters,
    numberInputOptions,
    handleFieldAChange,
    handleFieldChange,
    handleRemoveField,
  } = arithmeticState;

  const handleDim1FieldAChange = useCallback((id, v) => {
    if (!v) { handleFieldAChange(null); return; }
    handleFieldAChange(v);
  }, [handleFieldAChange]);

  const { widgetDataStore } = useReportLandingContext();
  const data = widgetDataStore?.[widgetConfig.id];
  const dim1Value = getVal(widgetConfig, DATA_KEYS.dimension1);

  const updateConfig = useCallback((updaterFn) => {
    setConfig((prev) => {
      let w = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      w = updaterFn(w);
      const full = [...prev];
      full[underEditWidgetIdx] = w;
      return full;
    });
  }, [setConfig, underEditWidgetIdx]);

  const visibilityModifier = useVisibilityModifier({
    dimension: 1,
    widgetConfig,
    dataKeys: DATA_KEYS,
    data,
    isNumberInput: dim1Value?.comp === 'NumberInput',
    updateConfig,
  });

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <AnimatePresence initial={false}>
          {sortedLetters.map((letter) => {
            const field = (
              <DataFieldInputSelector
                key={letter}
                indicator={sortedLetters.length > 1 ? { label: letter } : undefined}
                options={letter === 'A' ? optionsForDim1 : numberInputOptions}
                value={dataFields[letter]}
                onChange={(id, v) => {
                  if (letter === 'A') {
                    handleDim1FieldAChange(id, v);
                  } else {
                    handleFieldChange(letter, v);
                  }
                }}
                actions={sortedLetters.length > 1 ? [{ icon: 'X', onClick: () => handleRemoveField(letter), hoverColor: 'red' }] : []}
                placeholder="Select field"
                modifier={letter === 'A' ? visibilityModifier : undefined}
                picker={getPickerProp({
                  widgetComp: 'WidgetPieChart',
                  slot: letter === 'A' ? 'layerA' : 'numberFields',
                })}
              />
            );
            if (letter === 'A') return field;
            return (
              <CollapseTransitionItem key={letter} id={letter}>
                {field}
              </CollapseTransitionItem>
            );
          })}
        </AnimatePresence>
      </div>
    </>
  );
};

/** Exposed for ConfigForm/index.js to place in SectionWrapper headerRight */
export const Dimension1AddFieldButton = () => {
  const { arithmeticState } = usePieChartConfig();
  const { canAddFields, handleAddField, numberInputOptions, dataFields } = arithmeticState;
  const [showLightbox, setShowLightbox] = useState(false);
  const btnRef = useRef(null);

  return (
    <>
      <TooltipV2
        text={
          !dataFields.A
            ? "Add data to visualize"
            : canAddFields
              ? "Add additional number fields to include in this calculation"
              : "Activates only on number fields for arithmetic aggregation"
        }
        placement="top"
      >
        <span ref={btnRef}>
          <ButtonGhost
            icon="Plus"
            onClick={() => setShowLightbox(true)}
            disabled={!canAddFields}
            style={{ padding: "4px" }}
          />
        </span>
      </TooltipV2>
      {showLightbox && (
        <SearchableLightbox
          anchorRef={btnRef}
          onClose={() => setShowLightbox(false)}
          options={numberInputOptions}
          grouped={numberInputOptions.some(item => item.options && Array.isArray(item.options))}
          value={undefined}
          onChange={(_, selectedField) => {
            let enriched = selectedField;
            if (selectedField && !selectedField.categoryLabel) {
              const group = numberInputOptions.find(g =>
                Array.isArray(g.options) && g.options.some(o => o.value === selectedField.value)
              );
              if (group) enriched = { ...selectedField, categoryLabel: group.display };
            }
            handleAddField(enriched);
            setShowLightbox(false);
          }}
          searchPlaceholder="Search fields"
        />
      )}
    </>
  );
};
